import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import "./editor.scss";
import Switch from "react-switch";
import useKeyPress from "./useKeypress";
import Select, { components } from "react-select";
import Editor from "@monaco-editor/react";
import { ThreeDots } from "../../Loader/loader";
import { useDispatch } from "react-redux";
import {
  compileCode,
  submitCode,
  getNotebookUrl,
  submitNotebook,
  updateTestStatus,
} from "../../Redux/ActionCreators/candidateTestAction";
import { returnErrors } from "../../Redux/ActionCreators/errorAction";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/ace";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/theme-chaos";
import "ace-builds/src-noconflict/ext-language_tools.js";
import "ace-builds/src-noconflict/theme-idle_fingers";
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/mode-javascript";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/worker-html";
import "ace-builds/src-noconflict/worker-javascript";
import "ace-builds/src-noconflict/theme-textmate";
import ace from "ace-builds/src-noconflict/ace";

ace.config.set(
  "basePath",
  "/node_modules/ace-builds/src-noconflict"
);

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <img src="/images/select-arrow.svg" alt="" width="15px" />
    </components.DropdownIndicator>
  );
};

const Main = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const notebookConnected = useRef(false);
  const editorRef = useRef(null);
  const markerIdsRef = useRef([]);
  const lockAttachedRef = useRef(false);
  const latestValueRef = useRef("");

  const codingNav = props.codingNav;

  const [code, setCode] = useState("");
  const [value, setValue] = useState(code || "");
  const [processing, setProcessing] = useState(null);
  const [compile, setCompile] = useState(false);
  const [check, setCheck] = useState(false);
  const [theme, setTheme] = useState("vs-light");
  const [active, setActive] = useState("");
  const [submit, setSubmit] = useState(false);
  const [outputStatus, setOutputStatus] = useState(null);
  const [output, setOutput] = useState(null);
  const [parsedOutput, setParsedOutput] = useState([]);
  const [compileTestCases, setCompileTestCases] = useState([]);
  const [notebookLoading, setNotebookLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadingFailed, setLoadingFailed] = useState(false);
  const [canLoadIframe, setCanLoadIframe] = useState(false);
  const [notebookUrlLocal, setNotebookUrlLocal] = useState("");
  const [leftWidth, setLeftWidth] = useState("50%");
  const [connectTimer, setConnectTimer] = useState(180);
  const [showRetry, setShowRetry] = useState(false);
  const [runInput, setRunInput] = useState("");
  const [compail, setCompail] = useState(false)

  const retryLimit = 12;

  const [language, setLanguage] = useState(null);

  const starterCode = useSelector(state => state.candidateTest?.starterCode);
  const isStubEditor = props.question?.questionTypeID === 7 && starterCode?.useStub === true;

  const stub = starterCode?.stubs?.[0];

  const normalize = (s = "") => s.replace(/\r\n/g, "\n");

  const head = useMemo(() => normalize(stub?.head), [stub]);
  const tail = useMemo(() => normalize(stub?.tail), [stub]);

  const [editableBody, setEditableBody] = useState(normalize(stub?.body || ""));

  const visibleTestCases = compileTestCases.filter(tc => tc.hidden === false);
  const firstVisible = visibleTestCases[0];

  const passCount = visibleTestCases.filter(tc => tc.executionStatus === 1).length;
  const totalCount = visibleTestCases.length;

  const allPassed = totalCount > 0 && passCount === totalCount;

  const languageOptions =
    props.question?.questionTypeID === 12
      ? (props.question?.databaseLanguages?.length > 0 
          ? props.question?.databaseLanguages?.map((el) => ({
              label: el.displayLanguage,
              value: el.databaseLanguage,
            }))
          : props.question?.languages?.map((el) => ({
              label: el.displayLanguage,
              value: el.codingLanguage,
            }))
        )
      : props.question?.languages?.map((el) => ({
          label: el.displayLanguage,
          value: el.codingLanguage,
        }));

  const isSubmitted = props.question?.submitted === true;

  const isLastIndex = codingNav.currentIndex === codingNav.history.length - 1 && codingNav.history.length === props.question.totalQuestion;

  const headLines = useMemo(() => (head ? head.split("\n").length : 0),[head]);

  const tailLines = useMemo(() => (tail ? tail.split("\n").length : 0),[tail]);

  const extractBodyFromEditor = () => {
    if (!latestValueRef.current) return editableBody;

    const normalized = latestValueRef.current.replace(/\r\n/g, "\n");
    const lines = normalized.split("\n");

    return lines
      .slice(headLines, lines.length - tailLines)
      .join("\n");
  };

  const liveBody = extractBodyFromEditor();

  const sourceCode = isStubEditor ? `${head}${liveBody}${tail}` : code;

  const enterPress = useKeyPress("Enter");
  const ctrlPress = useKeyPress("Control");

  useEffect(() => {
    if (stub?.body && editableBody === "") {
      setEditableBody(normalize(stub.body));
    }
  }, [stub]);

  useEffect(() => {
    const reduxCode = props.question?.editorState?.code;

    console.log("🔵 [Main] editorState.code:", reduxCode);

    if (typeof reduxCode === "string") {
      setCode(reduxCode);
      setValue(reduxCode);
    } else {
      console.warn("⚠️ Non-string code received, resetting editor");
      setCode("");
      setValue("");
    }
  }, [props.question?.editorState?.code]);

  useEffect(() => {
    if (!props.question) return;

    const savedLang = props.question.editorState?.language;
    if (savedLang?.value) {
      setLanguage(savedLang);
      return;
    }

    if (languageOptions && languageOptions.length > 0) {
      setLanguage(languageOptions[0]);
    } else {
      setLanguage(null);
    }
  }, [props.question?.candidateQuestionId]);

  useEffect(() => {
    if (compile) {
      setTimeout(() => {
        const elem = document.querySelector(".complie-window");
        if (elem) {
          elem.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 200);
    }
  }, [compile, active]);

  const handleChangeMode = async (e) => {
    await setCheck(e);
    await setTheme(e ? "vs-dark" : "vs-light");
  };

  const onSelectChange = (sl) => {
    setLanguage(sl);
  };

  const handleEditorChange = (value) => {
    setValue(value);
    setCode(value);

    props.onCodeChange?.({
      code: value,
      language,
    });
  };

  useEffect(() => {
    if (enterPress && ctrlPress) {
      handleCompile();
    }
  }, [ctrlPress, enterPress]);

  const onChangeCode = (action, data) => {
    switch (action) {
      case "code": {
        setCode(data);
        setValue(data);
        break;
      }
      default: {
        console.warn(t("case not handled!"), action, data);
      }
    }
  };

  const handleCompile = async (e) => {
    e.preventDefault();
    if (props.question?.questionTypeID === 13) return;

    setProcessing(true);
    setCompail(true);
    setCompile(true);
    setActive("run");

    setOutputStatus(null);
    setCompileTestCases([]);
    setRunInput("");

    const liveBody = extractBodyFromEditor();

    const sourceCode = isStubEditor ? `${head}${liveBody}${tail}` : code;

    const formData = {
      token: props.inviteToken,
      candidateTestId: props.question?.candidateTestId,
      candidateTestQuestionId: props.question.candidateQuestionId,
      language: language?.value,
      sourceCode: sourceCode,
      inputs: props.question.inputs || [],
      output: "",
    };

    if (!language?.value) {
      setProcessing(false);
      setCompail(false);
      returnErrors(dispatch, t("Please select language"), 500);
    } else {
      const res = await dispatch(compileCode(formData, props.token));
      if (res?.type === "RUN_CODE") {
        setProcessing(false);
        setCompail(false);
        
        const oc = res?.payload?.ocResponse;
        
        setOutputStatus(oc);
        setCompileTestCases(res?.payload?.testCases || []);

        if (oc?.useInput && oc?.input) {
          setRunInput(oc.input);
        } else {
          setRunInput("");
        }
        
        setOutput(oc?.stdout);
        setParsedOutput(oc?.parsedOutput || []);
        onChangeCode("code", code);
      }
    }
    setProcessing(false);
    setCompail(false)
  };

  const handleSubmitNotebook = async () => {
    setProcessing(true);
    setCompail(true);

    const body = {
      token: props.inviteToken,
      candidateTestId: props.question?.candidateTestId,
      candidateTestQuestionId: props.question?.candidateQuestionId,
    };

    dispatch(submitNotebook(body, props.token));
    notebookConnected.current = false;
    setCanLoadIframe(false);
    setNotebookUrlLocal("");
    setConnectTimer(180);
    setProcessing(false);
    setCompail(false);
    await goNext();
  };

  const codeSubmit = async () => {
    if (isSubmitted) return;

    await setProcessing(true);

    if (props.question?.questionTypeID === 13) {
      return handleSubmitNotebook();
    }

    if (!language?.value) {
      returnErrors(dispatch, t("Please select language"), 500);
      setProcessing(false);
    } else {
      let obj = {
        token: props.inviteToken,
        candidateTestId: props.question?.candidateTestId,
        candidateTestQuestionId: props.question.candidateQuestionId,
        sourceCode: sourceCode,
        language: language?.value,
        inputs: props.question.inputs || [],
        output: "",
      };
      const res = await dispatch(submitCode(obj, props.token));
      if (res?.type === "SUBMIT_CODE") {
        dispatch({ type: "CODING_NAV_MARK_SUBMITTED" });

        const isLast =
          codingNav.currentIndex === codingNav.history.length - 1 &&
          codingNav.history.length === props.question.totalQuestion;

        props.onNext(persistEditorState());

        if (isLast) {
          await dispatch(
            updateTestStatus(
              {
                candidateTestDetailId: props.question?.candidateTestDetailId,
                status: 3,
              },
              props.token
            )
          );
          props.onTestCompleted?.();
          setProcessing(false);
          return;
        }
      }
    }
    await setProcessing(false);
  };

  const goNext = () => {
    props.getNext();
  };

  useEffect(() => {
    if (props?.question?.questionTypeID !== 13) return;

    notebookConnected.current = false;

    setCanLoadIframe(false);
    setNotebookUrlLocal("");
    setLoadingFailed(false);
    setRetryCount(0);

    setConnectTimer(180);
    setNotebookLoading(true);

    props.pauseTestTimer?.();
    loadNotebookUrl();
  }, [props?.question?.candidateQuestionId]);

  useEffect(() => {
    if (!notebookLoading) return;

    let interval = setInterval(() => {
      setConnectTimer((t) => (t > 0 ? t - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [notebookLoading]);

  const loadNotebookUrl = () => {
    dispatch(
      getNotebookUrl(
        props.inviteToken,
        props.question.candidateTestId,
        props.question.candidateQuestionId,
        props.token
      )
    ).then((res) => {
      const freshUrl = res?.payload;

      if (!freshUrl) {
        setLoadingFailed(true);
        setNotebookLoading(false);
        setConnectTimer(180);
        if (props.startTimer && props.question?.questionTypeID === 13) {
          props.startTimer();
        }
        return;
      }
      setNotebookUrlLocal(freshUrl);
      attemptNotebookLoad(freshUrl);
    });
  };

  const attemptNotebookLoad = async (url) => {
    if (!url) return;

    setNotebookLoading(true);

    const ok = await verifyNotebookUrl(url);

    if (ok) {

      notebookConnected.current = true;

      // Delay iframe mount by 10 seconds
      setTimeout(() => {
        setCanLoadIframe(true);
        setNotebookLoading(false);
      }, 15000);

      return;
    }

    // Retry logic
    if (retryCount < retryLimit - 1) {
      setRetryCount((prev) => prev + 1);

      setTimeout(() => {
        attemptNotebookLoad(url);
      }, 15000);
    } else {
      setLoadingFailed(true);
      setNotebookLoading(false);
      setConnectTimer(180);
      props.resumeTestTimer?.();
      if (!showRetry) {
        setShowRetry(true);
      }
    }
  };

  const handleIframeLoad = () => {
    if (!notebookConnected.current) {
      notebookConnected.current = true;
    }

    // After this moment: NEVER update any notebook state again.
    setNotebookLoading(false);
    setLoadingFailed(false);
    setConnectTimer(180);

    props.resumeTestTimer?.();

    if (props.uploadImage && props.question?.questionTypeID === 13) {
      props.uploadImage();
    }
  };

  const verifyNotebookUrl = async (url) => {
    try {
      const res = await fetch(url, { method: "GET" });
      if (res.ok) return true;

      return false;
    } catch {
      return false;
    }
  };

  const startDrag = (e) => {
    e.preventDefault();

    // Store container rect for accurate calculations
    const container = document.getElementById("resizeContainer");
    if (!container) return;

    const rect = container.getBoundingClientRect();

    const onMouseMove = (event) => {
      const x = event.clientX - rect.left;

      let newWidth = (x / rect.width) * 100;

      if (newWidth >= 20 && newWidth <= 80) {
        setLeftWidth(newWidth + "%");
      }
    };

    const stopDrag = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", stopDrag);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", stopDrag);
  };

  const persistEditorState = () => ({
    editorState: {
      code,
      language: language
        ? { label: language.label, value: language.value }
        : null,
    },
  });

  const onAceLoad = (editor) => {
    editorRef.current = editor;
    applyLocks(editor);

    if (!lockAttachedRef.current) {
      lockAttachedRef.current = true;

      editor.commands.on("beforeExec", (e) => {
        const command = e.command?.name;

        if (!["insertstring", "backspace", "del", "insertnewline"].includes(command)) {
          return;
        }

        const row = editor.getCursorPosition().row;
        const totalLines = editor.session.getLength();
        const bodyEnd = totalLines - tailLines;

        if (row < headLines || row >= bodyEnd) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    }
  };

  const applyLocks = (editor) => {
    const session = editor.getSession();
    const Range = ace.require("ace/range").Range;

    // Remove old markers safely
    markerIdsRef.current.forEach((id) => {
      session.removeMarker(id);
    });
    markerIdsRef.current = [];

    const bodyLines = editableBody.split("\n").length;

    // Head marker
    const headMarkerId = session.addMarker(
      new Range(0, 0, headLines, 0),
      "ace-readonly-section",
      "fullLine"
    );

    // Tail marker
    const tailMarkerId = session.addMarker(
      new Range(headLines + bodyLines, 0, Infinity, 0),
      "ace-readonly-section",
      "fullLine"
    );

    markerIdsRef.current.push(headMarkerId, tailMarkerId);
  };

  useEffect(() => {
    if (editorRef.current) {
      applyLocks(editorRef.current);
    }
  }, [editableBody]);

  return (
    <>
    <div
      className="coding-container"
      style={
        props.question?.questionTypeID === 13
        ? { width: "100%" }
        : { width: "90vw" }
      }
      >
      {props.question?.questionTypeID !== 13 && 
        <>
          <div className="coding-nav-bar d-flex justify-content-between" style={{margin:'5px 0'}}>
            <button
              className="submit-btn"
              disabled={codingNav.currentIndex === 0}
              onClick={() => props.onPrev(persistEditorState())}
              style={{background: codingNav.currentIndex === 0 ? "gray" : '', width:'180px'}}
            >
              Prev
            </button>
  
            {isSubmitted && (
              <span style={{ margin: "0 12px", color: "red" }}>
                This question has already been submitted
              </span>
            )}
  
            <button className="submit-btn" 
              style={{background: isLastIndex ? "gray" : '', width:'180px'}} 
              disabled={isLastIndex} 
              onClick={() => props.onNext(persistEditorState())}
            >
              Next
            </button>
          </div>
        </>
      }
      <div className="d-flex" style={{ border: "1px solid #D7DADB" }}>
        {props.question?.questionTypeID === 13 ? (
          <div
            id="resizeContainer"
            style={{
              display: "flex",
              width: "100%",
              height: "73vh",
              position: "relative",
            }}
          >
            <div
              id="leftPane"
              style={{
                width: leftWidth,
                minWidth: "20%",
                flexShrink: 0,
                padding: 15,
                borderRight: "2px solid #D7DADB",
                overflowY: "auto",
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center">
                  <h5 style={{ marginBottom: 0, fontSize: 18 }}>
                    {props.question?.currentQuestion}/
                    {props.question?.totalQuestion}
                  </h5>
                  <p style={{ marginLeft: 8, fontSize: 18, color: "#555" }}>
                    Data Science
                  </p>
                </div>
              </div>
              <h2>{props.question?.question}</h2>
              <p
                className="mt-3"
                dangerouslySetInnerHTML={{
                  __html: props.question?.description,
                }}
              />
            </div>
            <div
              onMouseDown={startDrag}
              style={{
                width: "6px",
                background: "linear-gradient(to bottom, #bbb, #888)",
                cursor: "col-resize",
                borderLeft: "2px solid #666",
                borderRight: "2px solid #666",
                height: "100%",
                flexShrink: 0,
              }}
            />
            <div
              id="rightPane"
              style={{
                flex: "1 1 auto",
                minWidth: "20%",
                overflow: "hidden",
                padding: 0,
              }}
            >
              {props.question?.questionTypeID === 13 ? (
                <Fragment>
                  <div style={{ borderBottom: "2px solid #D7DADB" }}></div>
                  <div className="lang-dd"></div>

                  <div style={{ borderBottom: "2px solid #D7DADB" }}></div>
                  <div className="mt-2" style={{ height: "64vh" }}>
                    {notebookLoading && (
                      <div
                        className="loading-overlay"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          background: "rgba(255,255,255)",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          zIndex: 20,
                        }}
                      >
                        <h3 style={{ color: "#000", marginBottom: 15 }}>
                          Connecting to Jupyter Notebook...
                        </h3>

                        <p style={{ fontSize: 18, marginBottom: 20 }}>
                          Time remaining: {connectTimer}s
                        </p>
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">
                            Connecting to notebook...
                          </span>
                        </div>
                      </div>
                    )}

                    {loadingFailed && (
                      <div
                        style={{
                          padding: 20,
                          color: "red",
                          textAlign: "center",
                        }}
                      >
                        Failed to load notebook after 3 attempts.
                        <br />
                        Please check your internet and try again.
                        <br />
                        {showRetry ? (
                          <>
                            <button
                              style={{
                                marginTop: 15,
                                padding: "10px 20px",
                                background: "#009199",
                                color: "white",
                                borderRadius: 6,
                                border: "none",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setLoadingFailed(false);
                                setRetryCount(0);
                                setShowRetry(false);
                                attemptNotebookLoad(notebookUrlLocal);
                              }}
                            >
                              Retry Connecting
                            </button>
                          </>
                        ) : (
                          <p>No more retries available.</p>
                        )}
                      </div>
                    )}

                    {notebookLoading && (
                      <div style={{ padding: 10, textAlign: "center" }}>
                        Retrying to connect to notebook
                        <br />
                        Attempt {retryCount + 1} of 3
                      </div>
                    )}

                    {canLoadIframe && notebookUrlLocal && (
                      <iframe
                        src={notebookUrlLocal}
                        title="Data Science Notebook"
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                        onLoad={handleIframeLoad}
                      />
                    )}
                  </div>
                  <div className="run-code-view">
                    <div className="run-header">
                      <button
                        className="submit-btn"
                        disabled={processing}
                        onClick={handleSubmitNotebook}
                        style={{
                          backgroundColor: processing ? "#D3D3D3" : "#f75891",
                          cursor: processing ? "not-allowed" : "pointer",
                        }}
                      >
                        {processing ? "Submitting..." : "SUBMIT NOTEBOOK"}
                      </button>
                    </div>
                  </div>
                </Fragment>
              ) : null}
            </div>
          </div>
        ) : (
          <Fragment>
            <div
              className="col-md-5"
              style={{
                padding: 15,
                borderRight: "2px solid #D7DADB",
                maxHeight: "73vh",
                overflowY: "auto",
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center">
                  <h5 style={{ marginBottom: 0, fontSize: 18 }}>
                    {props.question?.currentQuestion}/
                    {props.question?.totalQuestion}
                  </h5>
                  <p style={{ marginLeft: 8, fontSize: 18, color: "#555" }}>
                    {props.question?.questionTypeID === 13
                      ? "Data Science"
                      : props.question?.questionTypeID === 12
                      ? "Database"
                      : "Coding"}
                  </p>
                </div>
                {props.question?.testName && (
                  <div>
                    <strong style={{ fontSize: 18, color: "#333" }}>
                      {props.question?.testName}
                    </strong>
                  </div>
                )}
              </div>
              <h2>{props.question?.question}</h2>
              <p
                className="mt-3"
                dangerouslySetInnerHTML={{
                  __html: props.question?.description,
                }}
              />
            </div>
            <div className="col-md-7">
              {!submit ? (
                <Fragment>
                  <div className="lang-dd">
                    <div class="d-flex  align-items-center">
                      <Switch
                        onChange={(e) => {
                          handleChangeMode(e);
                        }}
                        width={30}
                        height={16}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        onColor="#02c573"
                        checked={check}
                      />
                      &nbsp;&nbsp;
                      <label className="col-form-label">{t("Dark Mode")}</label>
                      &nbsp;&nbsp;
                    </div>
                    <div
                      class="d-flex  align-items-center"
                      style={{ width: "40%" }}
                    >
                      <label className="col-form-label">{t("Language")}</label>
                      &nbsp;&nbsp;
                      <div style={{ width: "70%" }}>
                        <Select
                          components={{ DropdownIndicator }}
                          placeholder={`Filter By Category`}
                          options={languageOptions}
                          // styles={customStyles}
                          value={language}
                          onChange={(selectedOption) =>
                            onSelectChange(selectedOption)
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <img
                        src="/images/reset.svg"
                        alt=""
                        className="cursor-pointer"
                        onClick={() => onChangeCode("code", "")}
                      />
                    </div>
                  </div>
                  <div style={{ borderBottom: "2px solid #D7DADB" }}></div>
                  <div className="mt-2">
                    {isStubEditor ? (
                    <AceEditor
                      mode="python"
                      theme="textmate"
                      defaultValue={`${head}${editableBody}${tail}`}
                      width="100%"
                      height="60vh"
                      readOnly={false}
                      onLoad={onAceLoad}
                      onChange={(val) => {latestValueRef.current = val}}
                      setOptions={{
                        useWorker: false,
                        showPrintMargin: false
                      }}
                      style={{
                        opacity: 1
                      }}
                    />
                  ) : (
                    <Editor
                      height="60vh"
                      width={`100%`}
                      language={language?.value}
                      value={value}
                      theme={theme}
                      // defaultValue="// some comment"
                      onChange={handleEditorChange}
                      options={{
                        autoIndent: "full",
                        contextmenu: true,
                        fontFamily: "monospace",
                        lineHeight: 24,
                        hideCursorInOverviewRuler: true,
                        inlineSuggest: true,
                        fontSize: "16px",
                        formatOnType: true,
                        autoClosingBrackets: true,
                        minimap: { enabled: false },
                        scrollbar: {
                          horizontalSliderSize: 4,
                          verticalSliderSize: 18,
                        },
                        selectOnLineNumbers: true,
                        roundedSelection: false,
                        readOnly: isSubmitted,
                        cursorStyle: "line",
                        automaticLayout: true,
                      }}
                    />
                  )}

                    <div className="run-code-view">
                      <div className="run-header">
                        <button
                          className="cursor-pointer submit-btn"
                          disabled={processing || isSubmitted}
                          onClick={(e) => handleCompile(e)}
                          style={{background: (processing || isSubmitted) ? '#D3D3D3' : '', cursor: (processing || isSubmitted) ? "not-allowed" : "pointer",}}
                        >
                          {t("COMPILE AND RUN CODE")}
                        </button>
                        <button
                          className="submit-btn"
                          disabled={processing || isSubmitted}
                          onClick={() => codeSubmit()}
                          style={{
                            backgroundColor: (processing || isSubmitted) ? "#D3D3D3" : "#f75891",
                            cursor: (processing || isSubmitted) ? "not-allowed" : "pointer",
                          }}
                        >
                          {t("SUBMIT CODE")}
                        </button>
                      </div>
                      {compile ? (
                      <div className="complie-window" style={{ minHeight: "200px" }}>
                        {compail && (
                          <div
                            style={{
                              padding: 20,
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              fontSize: 18,
                              color: "#555",
                            }}
                          >
                            <ThreeDots color="#009199" />
                            <span>Compiling and running your code…</span>
                          </div>
                        )}
                        {active === "run" && outputStatus && (
                          <div>
                            <h4 style={{ marginBottom: 10 }}>Compile Result</h4>

                            {outputStatus.useInput && runInput && (
                              <div
                                style={{
                                  marginBottom: 12,
                                  padding: 10,
                                  borderRadius: 6,
                                  background:
                                    outputStatus.executionStatus === 1
                                      ? "#E6FFE6"
                                      : "#FFE6E6",
                                  border:
                                    outputStatus.executionStatus === 1
                                      ? "1px solid #2E8B57"
                                      : "1px solid #B22222",
                                  fontFamily: "monospace",
                                  whiteSpace: "pre-wrap",
                                  maxHeight:'200px',
                                  maxWidth: "100%",
                                  overflow: "auto",
                                }}
                              >
                                <strong>Input</strong>
                                <pre style={{ margin: "6px 0 0 0" }}>{runInput}</pre>
                              </div>
                            )}

                            <div
                              style={{
                                marginBottom: 12,
                                padding: 10,
                                borderRadius: 6,
                                background:
                                  outputStatus.executionStatus === 1
                                    ? "#E6FFE6"
                                    : "#FFE6E6",
                                border:
                                  outputStatus.executionStatus === 1
                                    ? "1px solid #2E8B57"
                                    : "1px solid #B22222",
                                fontFamily: "monospace",
                                whiteSpace: "pre-wrap",
                                maxHeight:'200px',
                                maxWidth: "100%",
                                overflow: "auto",
                              }}
                            >
                              <strong>Output</strong>
                              <pre style={{ margin: "6px 0 0 0", whiteSpace: "pre", minWidth: "max-content" }}>
                                {outputStatus.stdout || outputStatus.actualOutput || "—"}
                              </pre>
                            </div>

                            <div
                              style={{
                                fontWeight: "bold",
                                color:
                                  outputStatus.executionStatus === 1 ? "green" : "red",
                              }}
                            >
                              Status:{" "}
                              {outputStatus.executionStatus === 1
                                ? "Success"
                                : "Failed"}
                            </div>
                          </div>
                        )}

                        <div style={{ fontFamily: "monospace", marginTop: 10 }}>

                        {/* Summary line */}
                        {compileTestCases.length > 0 && (
                          <div
                            style={{
                              color: allPassed ? "#1B8E3E" : "#B00020",
                              fontWeight: 600,
                              fontSize: 16,
                              marginBottom: 12
                            }}
                          >
                            {allPassed
                              ? `✓ All sample testcases passed! (${passCount}/${totalCount})`
                              : `✗ Some sample testcases failed! (${passCount}/${totalCount})`}
                          </div>
                        )}

                        {/* Each testcase status */}
                        {compileTestCases.map((tc) => (
                          <div key={tc.testCaseId} style={{ marginBottom: 6 }}>
                            <strong>Testcase {tc.testCaseId} :</strong>{" "}
                            <span style={{ color: tc.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>
                              {tc.executionStatus === 1 ? "Passed" : "Failed"}
                            </span>
                          </div>
                        ))}

                        {/* Details only for visible testcase */}
                        {firstVisible && (
                          <>
                            <div style={{ marginTop: 14 }}>
                              <div style={{ marginBottom: 6 }}>
                                <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Status</strong>{" "}
                                <span style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>
                                  {firstVisible.executionStatus === 1 ? "Passed" : "Failed"}
                                </span>
                              </div>

                              <div style={{ marginBottom: 6 }}>
                                <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Execution time</strong>{" "}
                                <span style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>{outputStatus?.executionTime || "—"} ms</span>
                              </div>

                              <div style={{ marginBottom: 10 }}>
                                <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Description</strong>{" "}
                                <span style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>
                                  {firstVisible.executionStatus === 1
                                    ? "Testcase passed!"
                                    : "Output did not match expected result"}
                                </span>
                              </div>

                              {firstVisible.input && (
                                <div style={{ marginBottom: 10 }}>
                                  <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Input</strong>
                                  <pre style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>{firstVisible.input}</pre>
                                </div>
                              )}

                              <div style={{ marginBottom: 6 }}>
                                <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}> Output</strong>
                                <pre style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>{firstVisible.actualOutput || "—"}</pre>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      </div>
                    ) : null}
                    </div>
                  </div>
                </Fragment>
              ) : (
                <div className="text-center" style={{ padding: 150 }}>
                  <img src="/images/green-check.svg" alt="" />
                  <h6>
                    {t(
                      "Your code was submitted successfully. You are now being redirected to the next question"
                    )}
                  </h6>
                  <button
                    className="output-btn"
                    onClick={() =>
                      !props.isNextTest && !props.isTestCompleted
                        ? goNext()
                        : props.isTestCompleted
                        ? props.setActive("feedback")
                        : props.isNextTest
                        ? props.getSummary()
                        : null
                    }
                  >
                    {t("View Next Question")}
                  </button>
                </div>
              )}
            </div>
          </Fragment>
        )}
      </div>
    </div>
    </>
  );
};

export default Main;
